// ─────────────────────────────────────────────────────────────────────────────
// graphMath — deterministic primitives for the spatial graph.
//
// THE WHOLE POINT OF THIS FILE IS THAT Math.random NEVER APPEARS IN THE GRAPH.
// The macro geography must be learnable: the same graph must land in the same
// place after a reload, on another machine, in a headless capture. Every
// "irregular", "organic" or "jittered" value in the layout is derived from a
// node's own stable id through the hash below — so it is arbitrary-looking and
// completely reproducible at the same time.
//
// Only what G0 actually consumes lives here. A helper nobody calls is a lever
// someone eventually turns.
// ─────────────────────────────────────────────────────────────────────────────

/** xmur3 — string → uint32 seed. Cheap, well-mixed, and stable across engines. */
export function hashSeed(key: string): number {
  let h = 1779033703 ^ key.length;
  for (let i = 0; i < key.length; i++) {
    h = Math.imul(h ^ key.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

/**
 * mulberry32 — seeded PRNG. Returns a function; call it repeatedly for a stable
 * stream. Seed it from hashSeed(nodeId) so a node's stream belongs to the node
 * and not to the order it happened to be processed in.
 */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** One deterministic value in [0,1) for (id, salt). Order-independent. */
export function hashUnit(id: string, salt: string): number {
  return hashSeed(id + ' ' + salt) / 4294967296;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

/**
 * Spherical → cartesian in the graph's world frame.
 * azimuth  — radians around +Y (the "compass bearing" of a borough)
 * elevation — radians above/below the equator (the elevation band)
 */
export function sphericalToCartesian(
  azimuth: number,
  elevation: number,
  radius: number,
  out: [number, number, number],
): [number, number, number] {
  const ce = Math.cos(elevation);
  out[0] = Math.cos(azimuth) * ce * radius;
  out[1] = Math.sin(elevation) * radius;
  out[2] = Math.sin(azimuth) * ce * radius;
  return out;
}

export const TAU = Math.PI * 2;

/**
 * A deterministic point inside an ELLIPSOID VOLUME, not on its surface.
 *
 * Math.cbrt(w) is the whole trick: sampling radius uniformly would pile points
 * near the centre, and using radius 1 would put them all on the shell — which is
 * exactly what made the G1 clusters read as spokes with beads on the ends.
 */
export function sampleEllipsoidVolume(
  seed: string,
  radiusX: number,
  radiusY: number,
  radiusZ: number,
  out: [number, number, number],
): [number, number, number] {
  const u = hashUnit(seed, 'ell-u');
  const v = hashUnit(seed, 'ell-v');
  const w = hashUnit(seed, 'ell-w');

  const theta = TAU * u;
  const cosPhi = v * 2 - 1;
  const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
  const radius = Math.cbrt(w);

  out[0] = radiusX * radius * sinPhi * Math.cos(theta);
  out[1] = radiusY * radius * sinPhi * Math.sin(theta);
  out[2] = radiusZ * radius * cosPhi;
  return out;
}

/**
 * A stable orthonormal basis pointing from `origin` toward `anchor`.
 *
 * The reference axis is swapped when forward is near-vertical: crossing two
 * near-parallel vectors yields a near-zero right vector and the whole cluster
 * collapses into a line.
 */
export function localBasis(
  origin: [number, number, number],
  anchor: [number, number, number],
  right: [number, number, number],
  up: [number, number, number],
  forward: [number, number, number],
): void {
  let fx = anchor[0] - origin[0];
  let fy = anchor[1] - origin[1];
  let fz = anchor[2] - origin[2];
  let len = Math.sqrt(fx * fx + fy * fy + fz * fz);
  if (len < 1e-6) { fx = 0; fy = 0; fz = 1; len = 1; }
  fx /= len; fy /= len; fz /= len;

  const useWorldRight = Math.abs(fy) > 0.92;
  const rx = useWorldRight ? 1 : 0;
  const ry = 0;
  const rz = useWorldRight ? 0 : 1;

  // right = normalise(reference × forward)
  let cx = ry * fz - rz * fy;
  let cy = rz * fx - rx * fz;
  let cz = rx * fy - ry * fx;
  let clen = Math.sqrt(cx * cx + cy * cy + cz * cz) || 1;
  cx /= clen; cy /= clen; cz /= clen;

  right[0] = cx; right[1] = cy; right[2] = cz;
  forward[0] = fx; forward[1] = fy; forward[2] = fz;
  // up = forward × right
  up[0] = fy * cz - fz * cy;
  up[1] = fz * cx - fx * cz;
  up[2] = fx * cy - fy * cx;
}
